import {
  Button,
  formatCurrency,
  HorizontalDivider,
  Icon,
  Input,
  Notification,
  Typography,
  VerticalDivider
} from '@smartgift/frontend-toolkit';
import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { SkeletonLoaders } from '@hero/components';
import { PageLayout } from '@hero/components/layouts';
import { DuplicateModal, TotalEstimatedSpendModal } from '@hero/components/modals';
import { DetailGroup, DetailItem, ShowMore } from '@hero/components/molecules';
import { useValidationErrorsStore } from '@hero/components/providers/ValidationErrorsProvider';
import {
  APPROVAL_REASON_GIFT_CARD,
  APPROVAL_REASON_PUNCHOUT,
  APPROVAL_STATUSES,
  CARDISLE_GREETING_CARD,
  MAX_COST_CENTER_LENGTH,
  MAX_PO_LENGTH,
  PAYMENT_METHOD_TYPES,
  PRODUCT_TYPES,
  RECOGNITION_STATUSES,
  SEND_GIFT_FLOW_STEPS,
  SOURCE_TYPES
} from '@hero/constants';
import { useDeleteModalTemplate, useSelectedOrganization } from '@hero/hooks';
import { ROUTE_PATHS } from '@hero/routes';
import {
  useDeleteRecognition,
  useDuplicateRecognition,
  useGetRecognition,
  useGetRecognitionSpendingSummary,
  useStartRecognition,
  useSubmitToApproval,
  useUpdateRecognition
} from '@hero/services';
import {
  useGetPaymentMethods,
  useGetProviderUrl,
  useGetUser
} from '@hero/services/organization-service/hooks';
import { useNotification } from '@hero/store/hooks';
import { addPaymentMethodLink, getAddonData } from '@hero/utils';
import { openPopUp } from '@hero/utils/openPopup';

import HeaderNotificationContent from '../RecognitionReviewAndPayTemplate/HeaderNotificationContent';

import * as S from './style';

function SendGiftCheckoutTemplate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { recognitionCode } = useParams();
  const { showNotification } = useNotification();
  const { data: { paymentMethods = [] } = {}, refetch } = useGetPaymentMethods();
  const { data: recognition } = useGetRecognition(
    {
      recognitionCode
    },
    { includeGoLiveChecks: true }
  );
  const { addError } = useValidationErrorsStore();
  const organization = useSelectedOrganization();
  const { data: providerUrl } = useGetProviderUrl();
  const user = useGetUser();

  const {
    data: recognitionSpendingSummary,
    isFetching: isFetchingSpendingSummary,
    isLoading: isLoadingSpendingSummary,
    isError: isErrorSpendingSummary
  } = useGetRecognitionSpendingSummary({
    recognitionCode
  });
  const { mutateAsync: startRecognition, isLoading: isLoadingRecognition } = useStartRecognition();
  const { mutateAsync: submitToApproval, isLoading: isLoadingApproval } = useSubmitToApproval();

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState();
  const [isEstimatedTotalModalOpen, setIsEstimatedModalOpen] = useState(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [totalEstimatedSpendModal, setTotalEstimatedSpendModal] = useState(false);
  const [poNumber, setPONumber] = useState('');
  const [costCenter, setCostCenter] = useState('');

  const { mutateAsync: updateRecognition } = useUpdateRecognition();
  const { mutateAsync: deleteRecognition } = useDeleteRecognition();
  const { mutateAsync: duplicateRecognition } = useDuplicateRecognition();
  const { showDeleteModal } = useDeleteModalTemplate({
    title: t('areYouSureYouWantDelete', { type: t('oneTimeGift').toLowerCase() }),
    message: t('deleteRecognitionModalDesc'),
    confirmText: 'DELETE GIFT',
    maxLength: 18
  });
  const { showSuccess } = useNotification();

  const [localLoading, setLocalLoading] = useState(false);

  const isLoadingSummary = isFetchingSpendingSummary || isLoadingSpendingSummary;
  const isLoading =
    localLoading ||
    isFetchingSpendingSummary ||
    isLoadingSpendingSummary ||
    isLoadingRecognition ||
    isLoadingApproval;
  const isError = isErrorSpendingSummary;
  const featureType = 'recognition';
  const contactType = recognition?.contacts?.type;
  const digitalGiftCard = getAddonData(recognition?.addons, CARDISLE_GREETING_CARD);
  const estimatedCharges = recognitionSpendingSummary?.estimatedCharges;
  const minEstimatedCharge = estimatedCharges?.serviceCharges?.min;
  const maxEstimatedCharge = estimatedCharges?.serviceCharges?.max;
  const hasMinMaxEstimatedCharge =
    typeof minEstimatedCharge === 'number' && typeof maxEstimatedCharge === 'number';

  const isHouseAccount = useMemo(() => {
    return !!paymentMethods?.find(
      paymentMethod => paymentMethod.type === PAYMENT_METHOD_TYPES.houseAccount
    );
  }, [paymentMethods]);

  const approvalType = recognition?.approval?.reason;
  const isCCPayment = recognition?.paymentMethod?.type === PAYMENT_METHOD_TYPES.creditCard;
  const shouldShowEstimatedChargeRange = minEstimatedCharge === 0 && maxEstimatedCharge !== 0;
  const hasEGiftCardItem = recognition?.products?.some(
    p => p?.meta?.productType === PRODUCT_TYPES.GIFT_CARD
  );
  const isApprovalRequired =
    recognition?.approval?.status === APPROVAL_STATUSES.required &&
    recognition?.approvalStatus?.giftCard !== APPROVAL_STATUSES.rejected;
  const isApprovalPending = recognition?.status?.current === RECOGNITION_STATUSES.pending_approval;
  const isApprovalBoxShown = isApprovalRequired || isApprovalPending;
  const isEGiftCardRequired =
    recognition?.approval?.reason === PRODUCT_TYPES.GIFT_CARD && isApprovalRequired;

  const submitForApproval = async () => {
    if (approvalType === APPROVAL_REASON_PUNCHOUT) {
      const response = await submitToApproval({
        code: recognitionCode,
        approvalType: APPROVAL_REASON_PUNCHOUT
      });

      window.location.href = response.redirectLink;
      return;
    }

    if (approvalType === APPROVAL_REASON_GIFT_CARD) {
      await submitToApproval({
        code: recognitionCode,
        approvalType: APPROVAL_REASON_GIFT_CARD
      });

      showNotification({
        header: t('eGiftCardApprovalPending'),
        customContent: () => (
          <S.CustomApprovalNotificationContent>
            <Notification
              style={{ margin: '8px 0' }}
              status="info"
              message={
                <Trans i18nKey="eGiftCardWasCouldNotAddedRecognition" components={{ b: <b /> }} />
              }
            />
            <Icon name="clock" size="xxl" />
            <Typography>
              <Trans i18nKey="yourApprovalRequestIsReviewed" components={{ b: <b /> }} />
            </Typography>
          </S.CustomApprovalNotificationContent>
        )
      });
    }
  };

  const handleCancel = () => navigate(ROUTE_PATHS.SEND_GIFT_REVIEW(recognitionCode));

  const handleConfirm = async () => {
    setLocalLoading(true);
    let isAnyError = false;
    if (!poNumber && organization?.configs?.poNumberRequired) {
      isAnyError = true;
      addError(<Trans key="poNumberRequired" i18nKey="poNumberRequired" />);
    }
    if (!costCenter && organization?.configs?.costCenterRequired) {
      isAnyError = true;
      addError(<Trans key="costCenterRequired" i18nKey="costCenterRequired" />);
    }

    if (!selectedPaymentMethod && !isHouseAccount) {
      isAnyError = true;
      addError(<Trans key="paymentMethodRequired" i18nKey="paymentMethodRequired" />);
    }

    if (!isAnyError) {
      await updateRecognition({
        params: { recognitionCode: recognition?.code },
        body: {
          paymentMethod: selectedPaymentMethod,
          meta: {
            poNumber,
            costCenter
          }
        }
      });
      await startRecognition(recognitionCode);
      navigate(ROUTE_PATHS.SEND_GIFT_CONFIRMATION(recognitionCode), {
        replace: true
      });
    }
    setLocalLoading(false);
  };

  const getPrimaryButton = () => {
    const button = {
      loading: isLoading,
      children: t('payAndSend'),
      onClick: handleConfirm,
      icon: true
    };

    if (isApprovalBoxShown) {
      button.children = t('submitForApproval');
      button.onClick = async () => await submitForApproval();
      return button;
    }

    return button;
  };

  const payAndSendButtonProps = useMemo(
    () => ({
      icon: false,
      loading: isLoading,
      style: {
        width: '100%'
      },
      ...getPrimaryButton(),
      onClick: async () => {
        await getPrimaryButton().onClick();
      }
    }),
    [isLoading, isApprovalPending, recognition]
  );

  const getValue = value => {
    const hasEstimatedCharge = !!estimatedCharges?.serviceCharge || !!hasMinMaxEstimatedCharge;
    if (isError) {
      return 'N/A';
    } else if (isLoadingSummary || !hasEstimatedCharge) {
      return <SkeletonLoaders.CostSummaryLoader count={30} />;
    }
    return formatCurrency(value);
  };

  const onDelete = () => {
    showDeleteModal({
      onSubmit: async () => {
        await deleteRecognition(recognitionCode);
        navigate(ROUTE_PATHS.MY_RECOGNITIONS);
      }
    });
  };

  const onDuplicate = async formValues => {
    const response = await duplicateRecognition({
      code: recognition.code,
      body: {
        ...formValues,
        meta: {
          creationStep: SEND_GIFT_FLOW_STEPS.review
        }
      }
    });

    navigate(ROUTE_PATHS.SEND_GIFT_REVIEW(response.code));
  };

  useEffect(() => {
    if (recognition) {
      setSelectedPaymentMethod(
        paymentMethods?.find(
          paymentMethod => paymentMethod.code === recognition?.paymentMethod?.code
        )
      );
      setPONumber(recognition?.meta?.poNumber || '');
      setCostCenter(recognition?.meta?.costCenter || '');
    }
  }, [recognition, paymentMethods]);

  const orderedPaymentMethods = useMemo(() => {
    if (selectedPaymentMethod) {
      return [
        selectedPaymentMethod,
        ...paymentMethods.filter(pm => pm?.code !== selectedPaymentMethod?.code)
      ];
    }

    return paymentMethods;
  }, [selectedPaymentMethod, paymentMethods]);

  return (
    <PageLayout
      header={{
        title: t('sendGifts'),
        badgeChildren: (
          <Typography variant="body2SemiBold" color="fg" emphasis="weak">
            {t('giftCode')}:
            <Typography style={{ marginLeft: 4 }} as="span" variant="body2Bold">
              {recognitionCode}
            </Typography>
          </Typography>
        )
      }}
      navigation={{
        rightButton: payAndSendButtonProps,
        leftButton: {
          children: t('goBackToGiftEditing'),
          onClick: handleCancel
        }
      }}
    >
      <HeaderNotificationContent
        iseGiftCardApprovalBoxShown={isEGiftCardRequired && hasEGiftCardItem}
        recognitionCode={recognitionCode}
      />
      <S.Wrapper>
        <S.LeftContent>
          <S.TitleWrapper>
            <Typography variant="subtitle4SemiBold">{t('selectPaymentMethodText')}</Typography>
            {!isHouseAccount && (
              <>
                <VerticalDivider />
                <Button
                  startIcon={<Icon name="add" />}
                  onClick={() => {
                    openPopUp({
                      url: addPaymentMethodLink(providerUrl.loginLink, window.location.href),
                      target: 'editPaymentMethod',
                      onCallback: () => {
                        refetch();
                        showSuccess({
                          type: 'toast',
                          message: t('pmSuccessfullyAdded')
                        });
                      }
                    });
                  }}
                  variant="link"
                >
                  {t('addNewCard')}
                </Button>
              </>
            )}
          </S.TitleWrapper>
          {!isHouseAccount ? (
            orderedPaymentMethods.length ? (
              <ShowMore container={S.PaymentMethodArea} type="array" numberOfLines={6}>
                {orderedPaymentMethods?.map(paymentMethod => {
                  return (
                    <S.PaymentMethodItem
                      checked={paymentMethod?.code === selectedPaymentMethod?.code}
                      onClick={() => {
                        setSelectedPaymentMethod(paymentMethod);
                      }}
                      key={paymentMethod?.code}
                    >
                      <Typography
                        style={{
                          overflow: 'hidden',
                          lineClamp: 2,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {paymentMethod?.name}
                      </Typography>
                    </S.PaymentMethodItem>
                  );
                })}
              </ShowMore>
            ) : (
              <Typography variant="body2">{t('noPaymentMethodYet')}</Typography>
            )
          ) : (
            <Typography variant="body1" emphasis="weak" style={{ fontStyle: 'italic' }}>
              {t('yourOrganizationUsingHouseAccount')}
            </Typography>
          )}
          <S.GiftSettingArea>
            <Typography variant="subtitle4SemiBold">{t('giftSettings')}</Typography>
            <DetailGroup>
              {(recognition?.source !== SOURCE_TYPES.teams_app || !user?.meta?.isPunchoutUser) && (
                <>
                  <DetailItem
                    editable
                    title={`${t('po')}#:`}
                    bodyStyle={{ alignItems: 'center' }}
                    closeWithoutSave={() => {
                      setPONumber(recognition?.meta?.poNumber);
                    }}
                    saveDetail={() => {
                      updateRecognition({
                        params: { recognitionCode },
                        body: {
                          meta: {
                            poNumber,
                            costCenter
                          }
                        }
                      });
                    }}
                    editInput={() => (
                      <Input
                        value={poNumber}
                        inputProps={{
                          maxLength: MAX_PO_LENGTH,
                          onChange: e => {
                            setPONumber(e.target.value);
                          }
                        }}
                        formItem={{
                          label: t('poLabel'),
                          name: 'po'
                        }}
                      />
                    )}
                  >
                    {poNumber || t('notSet')}
                  </DetailItem>
                  <DetailItem
                    editable
                    bodyStyle={{ alignItems: 'center' }}
                    closeWithoutSave={() => {
                      setCostCenter(recognition?.meta?.costCenter);
                    }}
                    saveDetail={() => {
                      updateRecognition({
                        params: { recognitionCode },
                        body: {
                          meta: {
                            poNumber,
                            costCenter
                          }
                        }
                      });
                    }}
                    editInput={() => (
                      <Input
                        value={costCenter}
                        inputProps={{
                          maxLength: MAX_COST_CENTER_LENGTH,
                          onChange: e => {
                            setCostCenter(e.target.value);
                          }
                        }}
                        formItem={{
                          label: t('costCenterLabel'),
                          name: 'costCenter'
                        }}
                      />
                    )}
                    title={`${t('costCenter')}:`}
                  >
                    {costCenter || t('notSet')}
                  </DetailItem>
                </>
              )}
            </DetailGroup>
          </S.GiftSettingArea>
        </S.LeftContent>
        <S.RightContent>
          <Typography variant="subtitle4SemiBold">{t('costSummary')}</Typography>
          <Button
            variant="link"
            color="neutral"
            emphasis="fg"
            onClick={() => setTotalEstimatedSpendModal(true)}
          >
            {t('howOurPriceWork')}
          </Button>

          <S.Row style={{ marginTop: 24 }}>
            <Typography as="div" variant="body1SemiBold">
              {t('itemsEstimate') + ':'}
              <Typography variant="caption1" color="neutral" emphasis="400">
                ($
                {t('costSummarySubTitle1', {
                  recipientCount: estimatedCharges?.recipientCount
                })}
                )
              </Typography>
            </Typography>
            <S.Value>{getValue(estimatedCharges?.merchandise)}</S.Value>
          </S.Row>

          <S.Row style={{ marginTop: 16 }}>
            <Typography as="div" variant="body1SemiBold">
              {t('estimatedServiceAndShippingFees') + ':'}
              {!!shouldShowEstimatedChargeRange && (
                <Typography variant="caption1" color="fg" emphasis="weak">
                  {t('serviceChargeMessageForFreeShip')}
                </Typography>
              )}
            </Typography>
            {shouldShowEstimatedChargeRange ? (
              <S.Value>
                {getValue(minEstimatedCharge)} - {getValue(maxEstimatedCharge)}
              </S.Value>
            ) : (
              <S.Value>{getValue(estimatedCharges?.serviceCharge)}</S.Value>
            )}
          </S.Row>

          <S.Row style={{ marginTop: 16 }}>
            <Typography variant="body1SemiBold">{t('estimatedTax') + ':'}</Typography>
            <S.Value>{getValue(estimatedCharges?.estimatedTax)}</S.Value>
          </S.Row>

          <HorizontalDivider $margin="8px 0" />

          <S.TotalRow style={{ marginTop: 16 }}>
            <Typography as="div" variant="body1Bold">
              {t('totalEstimatedSpend')}:
            </Typography>
            <Typography style={{ alignSelf: 'flex-start' }} variant="body1ExtraBold">
              {getValue(estimatedCharges?.total)}
            </Typography>
          </S.TotalRow>

          <Button
            {...{
              ...payAndSendButtonProps,
              style: {
                width: '100%',
                marginTop: 24
              },
              variant: 'contained',
              color: 'primary',
              endIcon: <Icon name="next" />
            }}
            block
          />
          <Typography
            style={{
              display: 'flex',
              marginTop: 8,
              gap: 4,
              alignSelf: 'center',
              flexWrap: 'wrap',
              justifyContent: 'center',
              textAlign: 'center'
            }}
            variant="body2"
            emphasis="500"
          >
            <Trans
              i18nKey="youCanDuplicateOrDeleteX"
              values={{
                feature: featureType.toLocaleLowerCase()
              }}
              components={{
                button1: (
                  <Button
                    onClick={() => {
                      setDuplicateModalOpen(true);
                    }}
                    variant="link"
                    color="neutral"
                    emphasis="800"
                  />
                ),
                button2: <Button onClick={onDelete} variant="link" color="error" emphasis="800" />
              }}
            />
          </Typography>
        </S.RightContent>

        <TotalEstimatedSpendModal
          isCCPayment={isCCPayment}
          isModalOpen={isEstimatedTotalModalOpen}
          onModalOpen={() => setIsEstimatedModalOpen(false)}
        />
        <DuplicateModal
          isOpen={duplicateModalOpen}
          onOpen={setDuplicateModalOpen}
          onSubmit={onDuplicate}
          featureType={featureType}
          duplicateText={recognition?.name}
          recipientCount={estimatedCharges?.recipientCount}
          giftCount={recognition?.products?.length}
          contactType={contactType}
          giftMessage={recognition?.experienceSettings?.messageParams?.message}
          giftPrice={estimatedCharges?.baseProductPrice}
          digitalGiftCard={digitalGiftCard}
        />

        <TotalEstimatedSpendModal
          isCCPayment={isCCPayment}
          isModalOpen={totalEstimatedSpendModal}
          onModalOpen={() => setTotalEstimatedSpendModal(false)}
        />
      </S.Wrapper>
    </PageLayout>
  );
}

export { SendGiftCheckoutTemplate };
